import { IPoint3d } from '@/editor/math/IPoint3d';
import { EEntityType, ESceneMode, IEntityInfo } from '@/editor/types';
import { get2dCorner, get3dCameraFrontRightCorner } from '@/editor/entities/utils';
import { modelManager } from '@/editor/manager/entityManager';
import { IPoint2d } from '@/editor/math/IPoint2d';
import { renderPosToWorldPos } from '@/editor/utils';
import { Box3, Vector3 } from 'three';
import { getBox3Vertices } from '@/editor/math/utils';
import { pixelRatio2d } from '@/editor/math/const';
import { isMultiSelectDoorWindowOnly, isMultiSelectFurnitureOrAssemblyOnly } from '@/editor/utils/select/multi-select';
import { globalStores } from '@/editor/stores';

interface IResolveContextMenuAnchorParams {
  sceneMode: ESceneMode;
  entityInfo: IEntityInfo;
}

function isValidPoint(point: IPoint3d | undefined): point is IPoint3d {
  return !!point && Number.isFinite(point.x) && Number.isFinite(point.y) && Number.isFinite(point.z);
}

/**
 * 对象菜单锚点的旧实现，只在 FLAGS.FEATURES.CTX_MENU_POS_OPTIMIZE 关闭时使用。
 * 开启后走 cornerAnchorResolver 的角点方案。
 */
export function resolveContextMenuAnchor({
  sceneMode,
  entityInfo
}: IResolveContextMenuAnchorParams): IPoint3d | undefined {
  let point: IPoint3d | undefined;
  switch (sceneMode) {
    case ESceneMode.scene2d:
      point = getEntityCorner2d(entityInfo);
      break;
    case ESceneMode.scene3d:
      point = getEntityCorner3d(entityInfo);
      break;
  }

  if (isValidPoint(point)) {
    return point;
  }
}

function getEntityCorner2d(entityInfo: IEntityInfo): IPoint3d | undefined {
  const { type, entityId } = entityInfo;
  if (type === EEntityType.MultiModel) {
    const multiSelectData = globalStores.getDataByIdType('', EEntityType.MultiModel);
    if (!multiSelectData) return;
    // 门窗多选没有整体包围盒，锚在第一个门窗上
    if (isMultiSelectDoorWindowOnly(multiSelectData)) {
      const firstOne = multiSelectData.modelInfos
        .map((item) => modelManager.getEntity(item.id, item.type))
        .filter((item) => item !== undefined)[0];

      if (!firstOne) return;
      return getBoundingBoxCorner2d(firstOne.getBoundingBox());
    }

    // 家具、组合、隔墙及混合选中都有包围盒（隔墙的那部分由轮廓推导），取右上角
    const topRight: IPoint3d = {
      x: multiSelectData.center.x + multiSelectData.width / 2,
      y: multiSelectData.center.y + multiSelectData.thickness / 2,
      z: 0
    };
    return topRight;
  }

  switch (type) {
    case EEntityType.Region: {
      return getRightMostPoint(globalStores.getDataByIdType(entityId, type)?.pathInfos.map((info) => info.start));
    }
    case EEntityType.Wall: {
      return getRightMostPoint(globalStores.getDataByIdType(entityId, type)?.drawWallInfo.drawShape.flat());
    }
    case EEntityType.SeparateWall: {
      return getRightMostPoint(globalStores.getDataByIdType(entityId, type)?.drawSeparateWallInfo.drawShape);
    }
    default: {
      const boundingBox = modelManager.getEntity(entityId, type)?.getBoundingBox();
      if (!boundingBox) return;
      return getBoundingBoxCorner2d(boundingBox);
    }
  }
}

function getEntityCorner3d(entityInfo: IEntityInfo): IPoint3d | undefined {
  const { entityId, type } = entityInfo;
  if (type === EEntityType.MultiModel) {
    const multiSelectData = globalStores.getDataByIdType('', EEntityType.MultiModel);
    if (!multiSelectData) return;
    if (isMultiSelectFurnitureOrAssemblyOnly(multiSelectData)) {
      const boundingBox = new Box3();
      getBox3Vertices(multiSelectData).forEach((vertex) =>
        boundingBox.expandByPoint(
          new Vector3(vertex.x / pixelRatio2d, vertex.y / pixelRatio2d, vertex.z / pixelRatio2d)
        )
      );
      const corner = get3dCameraFrontRightCorner(boundingBox);

      return renderPosToWorldPos({
        x: corner.x,
        y: corner.y,
        z: corner.z
      });
    }

    return;
  }

  const entity = modelManager.getEntity(entityId, type);
  if (!entity) return;

  const boundingBox = entity.getBoundingBox3d();
  return getBoundingBoxCorner3d(boundingBox);
}

function getBoundingBoxCorner2d(boundingBox: Box3): IPoint3d {
  const topRightCorner = get2dCorner(boundingBox, 'top-right');
  return renderPosToWorldPos({
    x: topRightCorner.x,
    y: topRightCorner.y,
    z: topRightCorner.z
  });
}

function getBoundingBoxCorner3d(boundingBox: Box3): IPoint3d {
  const corner = get3dCameraFrontRightCorner(boundingBox);
  return renderPosToWorldPos({
    x: corner.x,
    y: corner.y,
    z: corner.z
  });
}

function getRightMostPoint(shape: IPoint2d[] | undefined): IPoint3d | undefined {
  if (!shape || shape.length === 0) return;

  let rightMost = shape[0];
  for (const point of shape) {
    if (point.x > rightMost.x) {
      rightMost = point;
    }
  }
  return { x: rightMost.x, y: rightMost.y, z: 0 };
}
